import { Controller } from '@hotwired/stimulus';
import AutoSave from 'stimulus/auto_save';

// Type, weight and dimensions are all knowable before the upload, so a picture that cannot
// be used is refused here rather than after a round trip.
export default class extends Controller {
  static targets = ['input', 'image', 'placeholder', 'emptyActions', 'filledActions', 'caption', 'message'];

  static values = {
    autoSaveUrl: String,
    maxSize: Number,
    minDimension: Number,
    constraints: String,
    required: Boolean
  };

  connect() {
    if (!this.autoSaveUrlValue) return;

    this.autoSave = new AutoSave({
      url: this.autoSaveUrlValue,
      name: this.inputTarget.name,
      debounce: 0,
      label: this.identifier,
      element: this.element,
      onSaved: value => this.dispatch('saved', { detail: { value } }),
      onError: message => this.showError(message)
    });
  }

  disconnect() {
    this.autoSave?.cancel();
    this.revokePreview();
  }

  browse() {
    this.inputTarget.click();
  }

  get file() {
    return this.hasInputTarget ? this.inputTarget.files[0] || null : null;
  }

  reset() {
    this.generation = (this.generation || 0) + 1;
    this.accepted = null;
    if (this.hasInputTarget) this.inputTarget.value = '';
    this.revokePreview();
    this.imageTarget.removeAttribute('src');
    this.showImage(false);
    this.clearError();
  }

  remove() {
    this.reset();
    this.markSatisfied(false);
    this.dispatch('removed');
  }

  // Measuring is asynchronous, so the pick is held out of the input until it passes: a form
  // submitted in the meantime carries the last file that did, not one still being read. The
  // generation guards a second pick landing while the first is still in flight.
  async selected() {
    const picked = this.inputTarget.files[0];
    if (!picked) return;

    const generation = this.generation = (this.generation || 0) + 1;
    this.restoreAccepted();

    const problem = await this.problemWith(picked);
    if (generation !== this.generation) return;

    if (problem) {
      this.showError(problem);
      return;
    }

    this.accepted = picked;
    this.hold(picked);
    this.clearError();
    this.preview(picked);
    this.caption(`${picked.name} · ${this.humanSize(picked.size)}`);
    this.markSatisfied(true);
    this.dispatch('change', { detail: { file: picked } });
    if (this.autoSave) this.autoSave.save(picked);
  }

  restoreAccepted() {
    if (!this.accepted) {
      this.inputTarget.value = '';
      return;
    }

    this.hold(this.accepted);
  }

  hold(file) {
    const carrier = new DataTransfer();
    carrier.items.add(file);
    this.inputTarget.files = carrier.files;
  }

  // The input is not rendered, so the browser can neither focus it to report a missing
  // answer nor show its bubble. The field says so itself instead.
  invalid(event) {
    event.preventDefault();
    this.showError("This field can't be empty.");
    this.element.querySelector('.ui-button')?.focus();
  }

  // An image already on the record satisfies the field, and a file input holding nothing
  // cannot be focused to say otherwise.
  markSatisfied(satisfied) {
    if (!this.hasInputTarget || !this.requiredValue) return;

    this.inputTarget.required = !satisfied;
  }

  async problemWith(file) {
    if (!['image/jpeg', 'image/png'].includes(file.type)) return 'Only JPG or PNG files are allowed';
    if (file.size > this.maxSizeValue) return 'File size must be less than 5 MB';

    return this.measure(file);
  }

  measure(file) {
    return new Promise(resolve => {
      const url = URL.createObjectURL(file);
      const probe = new Image();
      probe.onload = () => {
        URL.revokeObjectURL(url);
        const min = this.minDimensionValue;
        resolve(probe.width < min || probe.height < min ? `Image must be at least ${min}x${min}px` : null);
      };
      probe.onerror = () => {
        URL.revokeObjectURL(url);
        resolve('Could not read image file');
      };
      probe.src = url;
    });
  }

  preview(file) {
    this.revokePreview();
    this.previewUrl = URL.createObjectURL(file);
    this.imageTarget.src = this.previewUrl;
    this.showImage(true);
  }

  // A stored image whose URL no longer resolves leaves an empty circle.
  imageFailed() {
    this.showImage(false);
  }

  showImage(present) {
    this.imageTarget.hidden = !present;
    this.placeholderTarget.hidden = present;
    if (this.hasEmptyActionsTarget) this.emptyActionsTarget.hidden = present;
    if (this.hasFilledActionsTarget) this.filledActionsTarget.hidden = !present;
    if (!present) this.caption(this.constraintsValue);
  }

  caption(text) {
    if (this.hasCaptionTarget) this.captionTarget.textContent = text;
  }

  showError(message) {
    this.element.classList.add('ui-image-upload--error');
    if (!this.hasMessageTarget) return;

    this.messageTarget.textContent = message;
    this.messageTarget.hidden = false;
  }

  clearError() {
    this.element.classList.remove('ui-image-upload--error');
    if (!this.hasMessageTarget) return;

    this.messageTarget.textContent = '';
    this.messageTarget.hidden = true;
  }

  humanSize(bytes) {
    const kb = bytes / 1024;

    return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
  }

  revokePreview() {
    if (!this.previewUrl) return;

    URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = null;
  }
}
